import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BOOKING_STATUS_LABELS, BookingStatus, SystemRole, TRIP_TYPE_LABELS } from '@fleet/shared';
import { bookingsApi, type ManagedBooking } from '../../api/bookings';
import { vehiclesApi } from '../../api/vehicles';
import { useAuth } from '../../auth/useAuth';
import { formatDay, formatTimeRange } from '../../calendar/calendar-geometry';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge, ColorDot } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { DataTable } from '../../ui/DataTable';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { inputClass } from '../../ui/Field';

const STATUS_TONE: Record<BookingStatus, 'neutral' | 'amber' | 'emerald' | 'red' | 'blue'> = {
  [BookingStatus.PENDING_APPROVAL]: 'amber',
  [BookingStatus.APPROVED]: 'emerald',
  [BookingStatus.IN_PROGRESS]: 'blue',
  [BookingStatus.COMPLETED]: 'neutral',
  [BookingStatus.REJECTED]: 'red',
  [BookingStatus.CANCELLED]: 'neutral',
};

export function AllBookingsPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = auth.hasRole(SystemRole.ADMIN);

  const [status, setStatus] = useState<BookingStatus | ''>('');
  const [vehicleId, setVehicleId] = useState('');

  const query = useQuery({
    queryKey: ['all-bookings', { status, vehicleId }],
    queryFn: () =>
      bookingsApi.all({ status: status || undefined, vehicleId: vehicleId || undefined }),
  });
  const vehicles = useQuery({
    queryKey: ['vehicles', { includeInactive: true }],
    queryFn: () => vehiclesApi.list(true),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['all-bookings'] });
    await queryClient.invalidateQueries({ queryKey: ['availability'] });
    await queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
    await queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
  };

  const cancel = useMutation({
    mutationFn: (id: string) => bookingsApi.cancel(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => bookingsApi.remove(id),
    onSuccess: invalidate,
  });

  const isFinal = (booking: ManagedBooking) =>
    booking.status === BookingStatus.CANCELLED ||
    booking.status === BookingStatus.REJECTED ||
    booking.status === BookingStatus.COMPLETED;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Alle Buchungen</h2>
        <p className="text-sm text-slate-500">
          Stornieren gibt den Zeitraum frei und lässt nachvollziehbar, dass es die Buchung gab.
          Endgültiges Löschen ist für Fehleingaben und Testdaten gedacht — und nur möglich, solange
          keine Fahrt daran hängt.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value as BookingStatus | '')}
          className={`${inputClass} max-w-xs`}
          aria-label="Nach Status filtern"
        >
          <option value="">Alle Status</option>
          {Object.values(BookingStatus).map((value) => (
            <option key={value} value={value}>
              {BOOKING_STATUS_LABELS[value]}
            </option>
          ))}
        </select>

        <select
          value={vehicleId}
          onChange={(event) => setVehicleId(event.target.value)}
          className={`${inputClass} max-w-xs`}
          aria-label="Nach Fahrzeug filtern"
        >
          <option value="">Alle Fahrzeuge</option>
          {vehicles.data?.map((vehicle) => (
            <option key={vehicle.id} value={vehicle.id}>
              {vehicle.licensePlate} · {vehicle.displayName}
            </option>
          ))}
        </select>
      </div>

      {(cancel.isError || remove.isError) && <ErrorNotice error={cancel.error ?? remove.error} />}

      <AsyncBoundary query={query} empty="Keine Buchungen im gewählten Ausschnitt.">
        {(bookings) => (
          <DataTable
            rows={bookings}
            rowKey={(booking) => booking.id}
            rowClassName={(booking) => (isFinal(booking) ? 'text-slate-400' : '')}
            columns={[
              {
                header: 'Zeitraum',
                cell: (booking) => {
                  const start = new Date(booking.startsAt);
                  const end = new Date(booking.endsAt);
                  return (
                    <div className="tabular-nums">
                      <div>{formatDay(start)}</div>
                      {/*
                        Die zweite Zeile trägt den Endtag mit, wenn die Buchung
                        über Mitternacht geht — sonst stünde in der Spalte
                        „Zeitraum" nur ein einzelner Tag.
                      */}
                      <div className="text-xs text-slate-500">{formatTimeRange(start, end)}</div>
                    </div>
                  );
                },
              },
              {
                header: 'Fahrzeug',
                cell: (booking) => (
                  <div className="flex items-center gap-2">
                    <ColorDot color={booking.vehicle.colorHex} />
                    <span>{booking.vehicle.licensePlate}</span>
                  </div>
                ),
              },
              { header: 'Fahrer', cell: (booking) => booking.driver.displayName },
              {
                header: 'Art',
                cell: (booking) => (
                  <span className="text-xs">{TRIP_TYPE_LABELS[booking.tripType]}</span>
                ),
              },
              {
                header: 'Status',
                cell: (booking) => (
                  <div>
                    <Badge tone={STATUS_TONE[booking.status]}>
                      {BOOKING_STATUS_LABELS[booking.status]}
                    </Badge>
                    {booking.rejectionReason && (
                      <div
                        className="mt-0.5 max-w-xs truncate text-xs text-slate-500"
                        title={booking.rejectionReason}
                      >
                        {booking.rejectionReason}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                header: 'Nummer',
                cell: (booking) => (
                  <span className="font-mono text-xs text-slate-400">{booking.bookingNumber}</span>
                ),
              },
              {
                header: '',
                align: 'right',
                cell: (booking) => (
                  <div className="flex justify-end gap-1">
                    {!isFinal(booking) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={cancel.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `${booking.bookingNumber} stornieren?\n\nDer Zeitraum wird dadurch sofort wieder frei.`,
                            )
                          ) {
                            cancel.mutate(booking.id);
                          }
                        }}
                      >
                        Stornieren
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `${booking.bookingNumber} endgültig löschen?\n\nDie Buchung verschwindet vollständig. Für den Regelfall ist Stornieren die bessere Wahl — es gibt den Zeitraum ebenso frei.`,
                            )
                          ) {
                            remove.mutate(booking.id);
                          }
                        }}
                      >
                        Löschen
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}
      </AsyncBoundary>
    </div>
  );
}
